import axios from "axios";

/**
 * Lightweight Firestore REST integration for the status / priority banner system.
 *
 * These endpoints are PUBLIC read-only Firestore documents served over plain
 * HTTP GET, so no Firebase SDK / auth is required. If the env vars are not set
 * the helpers simply resolve to `null` and the banners stay hidden - the rest of
 * the app is never affected.
 */

const MAINTENANCE_URL = process.env.REACT_APP_FIREBASE_PROJECT_FETCH_DATA_LINK || "";
const PRIORITY_BANNER_URL = process.env.REACT_APP_FIREBASE_PROJECT_PRIORITY_BANNER_DATA_LINK || "";

console.log("PRIORITY_BANNER_URL:", PRIORITY_BANNER_URL);

// Resolve a Firestore REST "typed value" object ({ stringValue }, { booleanValue }, ...)
const readString = (field) => (field && field.stringValue) || "";
const readBool = (field) => {
	if (!field) return false;
	if (typeof field.booleanValue === "boolean") return field.booleanValue;
	return field.stringValue === "true";
};
const readTimestamp = (field) => (field && (field.timestampValue || field.stringValue)) || "";

const sortByCreatedAtDesc = (a, b) => {
	const dateA = new Date(a.createdAt).getTime() || 0;
	const dateB = new Date(b.createdAt).getTime() || 0;
	return dateB - dateA;
};

/**
 * Fetch the single most recent, active, non-completed "Scheduled" maintenance
 * document. Returns `null` when nothing qualifies or the request fails.
 */
export const fetchLatestScheduledMaintenance = async () => {
	if (!MAINTENANCE_URL) return null;

	try {
		const response = await axios.get(MAINTENANCE_URL);
		const documents = (response && response.data && response.data.documents) || [];

		const parsedDocs = documents.map((doc) => {
			const fields = doc.fields || {};
			const docId = doc.name ? doc.name.split("/").pop() : "";

			return {
				id: docId,
				type: readString(fields.type),
				isActive: readBool(fields.isActive),
				startDate: readString(fields.startDate),
				endDate: readString(fields.endDate),
				timezone: readString(fields.timezone) || "EST",
				status: readString(fields.status),
				title: readString(fields.title),
				link: readString(fields.link),
				createdAt: readTimestamp(fields.createdAt),
				updatedAt: readTimestamp(fields.updatedAt),
			};
		});

		const activeScheduledDocs = parsedDocs.filter((doc) => {
			const isScheduled = (doc.type || "").toLowerCase() === "scheduled";
			const isCompleted = (doc.status || "").toLowerCase() === "completed";
			const isActive =
				(doc.isActive === true || (doc.status || "").toLowerCase() === "active") && !isCompleted;
			return isScheduled && isActive;
		});

		if (activeScheduledDocs.length === 0) return null;

		activeScheduledDocs.sort(sortByCreatedAtDesc);
		return activeScheduledDocs[0];
	} catch (error) {
		console.error("Error fetching maintenance status from Firestore:", error);
		return null;
	}
};

/**
 * Fetch the single most recent active priority (incident / system update) banner.
 * Returns `null` when nothing qualifies or the request fails.
 */
export const fetchLatestPriorityBanner = async () => {
	if (!PRIORITY_BANNER_URL) return null;

	try {
		const response = await axios.get(PRIORITY_BANNER_URL);
		console.log("fetchLatestPriorityBanner response:", response);
		const documents = (response && response.data && response.data.documents) || [];
		console.log("fetchLatestPriorityBanner documents:", documents);

		const parsedDocs = documents.map((doc) => {
			const fields = doc.fields || {};
			const docId = doc.name ? doc.name.split("/").pop() : "";

			return {
				id: docId,
				isActive: readBool(fields.isActive),
				title: readString(fields.title),
				type: readString(fields.type),
				link: readString(fields.link),
				createdAt: readTimestamp(fields.createdAt),
				updatedAt: readTimestamp(fields.updatedAt),
			};
		});

		const activeBanners = parsedDocs.filter((doc) => doc.isActive === true);
		if (activeBanners.length === 0) return null;

		activeBanners.sort(sortByCreatedAtDesc);
		return activeBanners[0];
	} catch (error) {
		console.error("Error fetching priority banner from Firestore:", error);
		return null;
	}
};
